<template>
  <VueMarkdown
    class='clpp-markdown'
    :typographer='false'
    lang-prefix="language-"
    :breaks="false"
    :source='resolvedContent'
  >
    <slot></slot>
  </VueMarkdown>
</template>

<script>
import VueMarkdown from 'vue-markdown';
import Prism from 'prismjs';
import { Docs } from '../utils/docs';
import { getManualPageByFilename } from '../utils/manual';

/**
 * Convert text to ID e.g. "Hello  World" -> "hello-world".
 *
 * @param {string} text
 * @return {string} ID
 */
const toId = (text) => {
  return text
    .trim()
    .replace(/\s+/gm, '-') // Replace spaces with dashes
    .replace(/[^a-zA-Z0-9-._~]/g, '') // Remove invalid chars
    .toLowerCase();
};

/**
 * @return {HTMLSpanElement}
 */
const createAnchorIcon = () => {
  const result = document.createElement('span');
  result.innerHTML = `
    <svg aria-hidden="true" height="16" version="1.1" viewBox="0 0 16 16" width="16">
      <path fill-rule="evenodd"
        d="M4 9h1v1H4c-1.5 0-3-1.69-3-3.5S2.55 3 4 3h4c1.45 0 3 1.69 3 3.5 0 1.41-.91 2.72-2 3.25V8.59c.58-.45 1-1.27 1-2.09C10 5.22 8.98 4 8 4H4c-.98 0-2 1.22-2 2.5S3 9 4 9zm9-3h-1v1h1c1 0 2 1.22 2 2.5S13.98 12 13 12H9c-.98 0-2-1.22-2-2.5 0-.83.42-1.64 1-2.09V6.25c-1.09.53-2 1.84-2 3.25C6 11.31 7.55 13 9 13h4c1.45 0 3-1.69 3-3.5S14.5 6 13 6z"></path>
    </svg>
  `.trim();
  result.className = 'anchor-icon';
  return result;
};

const replaceMarkdownLinkUrls = (text, urlTransformer) => {
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
  return text.replace(regex, (match, linkText, url) => {
    return `[${linkText}](${urlTransformer(url)})`;
  });
};

const resolveManualInternalLinks = (markdown, currentRoute) => {
  return replaceMarkdownLinkUrls(markdown, (url) => {
    // Match URLs like "<filename>.md#some-hash" or "#some-hash"
    const regex = /^([^/#]+\.md)(?:#([^#]+))?$|^#([^#]+)$/;
    const match = url.match(regex);
    if (!match) {
      return url; // Return the original URL if no match is found
    }
    if (match[1]) {
      // Case: '<filename>.md' optionally followed by '#some-hash'
      const filename = match[1];
      const hash = match[2];

      // Find the corresponding path in the manual
      const page = getManualPageByFilename(filename);

      // If no manual page is found, return the original URL
      if (!page) {
        return url;
      }
      // If a manual page is found, return the full path to it and add
      // the hash if present
      return hash ? `/docs/${page.path}#${hash}` : `/docs/${page.path}`;
    } else if (match[3]) {
      // Case: Only '#hash' is present. Return the current path with the
      // hash appended.
      return `${currentRoute.path}#${match[3]}`;
    }
  });
};

export default {
  name: 'Markdown',
  props: ['content'],
  components: {
    VueMarkdown
  },
  computed: {
    resolvedContent () {
      let output = this.content;
      if (output) {
        output = Docs.resolve(output);
        output = resolveManualInternalLinks(output, this.$route);
      }
      return output;
    }
  },
  updated () {
    this.enableCodeHighlighting(this.$el);
    this.addRouterPushToRelativeLinks(this.$el);
    this.activateSectionAnchors_(this.$el);
  },
  mounted () {
    this.enableCodeHighlighting(this.$el);
    this.addRouterPushToRelativeLinks(this.$el);
    this.activateSectionAnchors_(this.$el);
  },
  methods: {
    addRouterPushToRelativeLinks (page) {
      /**
       * To achieve fluent redirection between Playmaker routes
       * we are using pushRouterPath registered on window object.
       * This method uses Vue router's push method.
       */

      if (!page) return;
      const aTags = page.querySelectorAll('a');
      aTags.forEach((aTag) => {
        const path = aTag.getAttribute('href');
        if (path && path.startsWith('/') && !aTag.onclick) {
          aTag.setAttribute('onclick', `pushRouterPath('${path}')`);
          aTag.setAttribute('style', 'cursor:pointer;');
          aTag.removeAttribute('href');
        }
      });
    },
    enableCodeHighlighting (page) {
      if (!page) return;
      Prism.highlightAllUnder(page);
      this.activateMultiLanguageCodeBlocks_(page);
    },
    /**
     * Make sure sections can be bookmarked.
     *
     * Make h1-h6 tags clickable. If it is clicked jump to it
     * and update the URL hash.
     */
    activateSectionAnchors_ (page) {
      if (!page) return;
      const hTags = page.querySelectorAll('h1, h2, h3, h4, h5, h6');
      hTags.forEach((hTag) => {
        if (hTag.id) {
          // h1-h6 tags generated from MD should not have an ID
          return;
        }

        hTag.id = toId(hTag.textContent);

        const anchorIcon = createAnchorIcon();
        anchorIcon.onclick = () => {
          const url = new URL(location);
          url.hash = `#${hTag.id}`;
          history.pushState({}, '', url);
          location.replace(url);
        };

        hTag.appendChild(anchorIcon);
      });
    },
    /**
     * Activate multi language code blocks - code blocks where user can
     * click to select the language of the code block.
     *
     * Syntax in MD:
     *
     * <div class="multi-lang"/>
     * ```js
     *  // some JS code
     * ```
     * ```HTML
     *  // some HTML code
     * ```
     * </div>
     */
    activateMultiLanguageCodeBlocks_ (page) {
      const multiBlocks = this.getNewMultiLanguageCodeBlocks_(page);
      multiBlocks.forEach(multiBlock => {
        const codeBlocks = Array.from(multiBlock.children)
          .filter(child => (child.tagName === 'PRE') ||
            child.classList.contains('code-block'));

        if (codeBlocks.length <= 1) return;

        let selectedIndex = 0;
        const { picker, options } = this.createPicker_(codeBlocks);

        function render () {
          codeBlocks.forEach((codeBlock, index) => {
            codeBlock.style.display = index === selectedIndex ? 'block' : 'none';
          });

          options.forEach(option => {
            option.style.backgroundColor = Number(option.dataset.index) === selectedIndex ? 'white' : 'rgb(189 189 184)';
          });
        }

        options.forEach((option, index) => {
          option.onclick = () => {
            selectedIndex = index;
            render();
          };
        });

        multiBlock.style.position = 'relative';
        multiBlock.appendChild(picker);
        render();
      });
    },
    /**
     * @param {HTMLElement} page currently rendered manual page
     * @return {HTMLPreElement[]}
     */
    getNewMultiLanguageCodeBlocks_ (page) {
      if (!page) return [];
      const allBlocks = Array.from(page.getElementsByClassName('multi-lang'));
      const newBlocks = allBlocks.filter(block => block.dataset.active !== true);
      newBlocks.forEach(block => { block.dataset.active = true });
      return newBlocks;
    },
    /**
     * @param {HTMLPreElement[]} codeBlocks elements such as <pre class="language-js">...</pre>
     *  or <div class="code-block language-js">...</div>
     */
    createPicker_ (codeBlocks) {
      const picker = document.createElement('div');
      picker.className = 'multi-lang-picker';
      picker.style.position = 'absolute';
      picker.style.right = '0px';
      picker.style.top = '8px';
      picker.style.backgroundColor = 'rgba(255, 255, 255, 1)';

      const options = codeBlocks.map((codeBlock, index) => {
        const option = document.createElement('span');
        option.dataset.index = index;
        const languageClass = Array.from(codeBlock.classList.values())
          .find(className => className.startsWith('language-')) || 'language-js';
        option.textContent = languageClass.replace('language-', '')
          .toUpperCase();
        option.style.padding = '5px';
        option.style.cursor = 'pointer';
        picker.appendChild(option);
        return option;
      });

      return {
        picker,
        options,
      };
    }
  }
};
</script>

<style lang="css">
.manual-page .multi-lang>pre:not(:first-child) {
  display: none;
}

.manual-page .multi-lang>.code-block:not(:first-child) {
  display: none;
}

.manual-page .anchor-icon {
  margin-left: 8px;
  opacity: 0;
  cursor: pointer;
  position: relative;
  top: 1px;
}

.manual-page h1:hover .anchor-icon,
.manual-page h2:hover .anchor-icon,
.manual-page h3:hover .anchor-icon,
.manual-page h4:hover .anchor-icon,
.manual-page h5:hover .anchor-icon,
.manual-page h6:hover .anchor-icon {
  opacity: 0.7;
}
</style>
